import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutline';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Stack,
	Theme,
	Typography,
	useMediaQuery,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Fragment, useCallback, useMemo, useState } from 'react';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import { getUrlFileSetDownload } from 'api/ApiUrls';
import { useProductCenterApi } from 'api/ApplicationApi';
import { unitInfoQuery } from 'api/queries';
import { healthCheckKeys } from 'api/queries/HealthCheckQueries';
import type {
	FileSetDto,
	ImportAdditionalFilesToHealthCheckCommand,
} from 'api/v1/data-contracts';
import UserIcon from 'assets/volvoIcons/People/UserIcon';
import { FileDisplayBase } from 'library/components/controls/files/FileDisplayBase';
import { getFileType } from 'library/components/controls/files/functions';
import { TransferFilesDialogActions } from 'modules/UnitOverview/Components/TransferFilesDialogActions';
import { TransferFilesStatusContent } from 'modules/UnitOverview/Components/TransferFilesStatusContent';

const groupGridSx = {
	display: 'grid',
	alignItems: 'center',
	gap: 2,
	gridTemplateAreas: {
		xs: `"checks files"
		"checks comment"
		"checks user"`,
		md: '"checks files comment user"',
	},
	gridTemplateColumns: {
		xs: 'auto 1fr',
		md: 'auto 6fr 7fr 3fr',
	},
} as const;

type HealthCheckTransferFilesProps = {
	unitId: number;
};

export const HealthCheckTransferFiles = ({
	unitId,
}: HealthCheckTransferFilesProps): React.JSX.Element => {
	const { t } = useTranslation([
		'fileUpload',
		'common',
		'commonButton',
		'commonWizard',
		'component',
		'unitOverview',
	]);

	const { api } = useProductCenterApi();
	const queryClient = useQueryClient();
	const { enqueueSnackbar } = useSnackbar();

	const fullScreen = useMediaQuery((theme: Theme) =>
		theme.breakpoints.only('xs')
	);

	const [open, setOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

	const {
		data: unitInfo,
		isLoading,
		isError,
	} = useQuery({
		...unitInfoQuery(api, unitId),
		enabled: !!unitId && open,
	});

	const fileSets = useMemo<FileSetDto[]>(
		() => unitInfo?.allFileSets ?? [],
		[unitInfo]
	);

	const handleClose = useCallback(() => {
		setOpen(false);
		setSelectedIds(new Set());
	}, []);

	const transferMutation = useMutation({
		mutationFn: (command: ImportAdditionalFilesToHealthCheckCommand) =>
			api.importAdditionalFilesToHealthCheck(command.unitId, command),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: healthCheckKeys.files(unitId),
				}),
				queryClient.invalidateQueries({
					queryKey: unitInfoQuery(api, unitId).queryKey,
				}),
			]);
			enqueueSnackbar(t('unitOverview:file-transfer-successful'), {
				variant: 'success',
			});
			handleClose();
		},
	});

	const handleOpen = () => {
		setSelectedIds(new Set());
		setOpen(true);
	};

	const toggleGroup = (fileSetId: number) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(fileSetId)) {
				next.delete(fileSetId);
			} else {
				next.add(fileSetId);
			}
			return next;
		});
	};

	const handleTransfer = () => {
		const selectedFileSets = fileSets.filter((fileSet) =>
			selectedIds.has(fileSet.id)
		);
		if (selectedFileSets.length === 0) return;
		transferMutation.mutate({
			unitId,
			fileSets: selectedFileSets.map((fileSet) => ({
				fileSetId: fileSet.id,
				comment: null, //BE handling
			})),
		});
	};

	const selectedCount = selectedIds.size;

	let content: React.ReactNode;

	if (!isError && !isLoading && fileSets.length > 0) {
		content = (
			<Box>
				<Typography variant="subtitle1" sx={{ mb: 2 }}>
					{t('unitOverview:transfer-files-helper-text')}
				</Typography>
				{fileSets.map((fileSet, index) => (
					<Fragment key={fileSet.id}>
						{index > 0 && <Divider sx={{ my: 2 }} />}
						<Box sx={groupGridSx}>
							<Box sx={{ gridArea: 'checks' }}>
								<Checkbox
									checked={selectedIds.has(fileSet.id)}
									onChange={() => toggleGroup(fileSet.id)}
									size="small"
								/>
							</Box>
							<Stack
								direction="column"
								spacing={2}
								sx={{ gridArea: 'files', alignSelf: 'start' }}>
								{fileSet.files.map((file) => (
									<FileDisplayBase
										key={file.id}
										name={file.fileName}
										url={getUrlFileSetDownload(
											fileSet.id,
											file.id
										)}
										type={getFileType(file.fileName) ?? ''}
									/>
								))}
							</Stack>
							<Box
								sx={{
									gridArea: 'comment',
									color: 'text.secondary',
								}}>
								{!fileSet.comment?.trim() && (
									<ChatBubbleOutline
										color="disabled"
										sx={{ mr: 1, verticalAlign: 'bottom' }}
									/>
								)}
								<Typography component="span">
									{fileSet.comment ?? ''}
								</Typography>
							</Box>
							<Typography
								sx={{
									gridArea: 'user',
									color: 'text.secondary',
								}}>
								<UserIcon
									color="primary"
									opacity={0.4}
									sx={{ mr: 1, verticalAlign: 'bottom' }}
								/>
								{fileSet.submittedBy}
							</Typography>
						</Box>
					</Fragment>
				))}
			</Box>
		);
	}

	return (
		<>
			<Button
				startIcon={<SwapHoriz />}
				color="secondary"
				disabled={!unitId}
				onClick={handleOpen}>
				{t('unitOverview:transfer-file')}
			</Button>

			<Dialog
				maxWidth="lg"
				fullWidth
				open={open}
				onClose={handleClose}
				fullScreen={fullScreen}>
				<DialogTitle>
					{t('unitOverview:select-files-title')}
					{selectedCount > 0 && ` (${selectedCount})`}
				</DialogTitle>
				<DialogContent>
					<TransferFilesStatusContent
						isError={isError}
						isLoading={isLoading}
						isEmpty={fileSets.length === 0}>
						{content}
					</TransferFilesStatusContent>
				</DialogContent>
				<TransferFilesDialogActions
					onClose={handleClose}
					onTransfer={handleTransfer}
					isPending={transferMutation.isPending}
					selectedCount={selectedCount}
				/>
			</Dialog>
		</>
	);
};
